import { supabase } from "@/integrations/supabase/client";
import { RealtimeChannel } from "@supabase/supabase-js";

/**
 * Track a table scan
 * @param tableId - The table ID being scanned
 * @returns Promise with scan result
 */
export const trackTableScan = async (
  tableId: string
): Promise<{
  success: boolean;
  daily_scans?: number;
  total_scans?: number;
  error?: string;
}> => {
  try {
    if (!tableId) {
      console.warn("No table ID provided");
      return { success: false, error: "No table ID provided" };
    }

    // First, validate if this is actually a table ID by checking existence
    const { data: tableExists, error: tableExistsError } = await supabase
      .from("store_tables")
      .select("id")
      .eq("id", tableId)
      .maybeSingle();

    if (tableExistsError) {
      console.error("Error validating table ID:", tableExistsError);
      return {
        success: false,
        error: "Error validating table ID: " + tableExistsError.message,
      };
    }

    if (!tableExists) {
      console.warn(`ID ${tableId} is not a valid table ID`);

      // Check if this might be a store ID instead
      const { data: storeData } = await supabase
        .from("stores")
        .select("name")
        .eq("id", tableId)
        .maybeSingle();

      if (storeData) {
        return {
          success: false,
          error: `This appears to be a store ID (${storeData.name}), not a table ID. Please scan a table QR code instead.`,
        };
      }

      return {
        success: false,
        error:
          "Invalid table ID - this might be a store ID or other identifier instead of a table ID",
      };
    }

    // Now proceed with tracking the scan
    const { data, error } = await supabase.rpc("track_table_scan", {
      table_id: tableId,
    });

    if (error) {
      console.error("Error tracking table scan:", error);
      return { success: false, error: error.message };
    }

    // Also fetch the updated scan counts
    const { data: tableData, error: tableError } = await supabase
      .from("store_tables")
      .select("daily_scans, total_scans")
      .eq("id", tableId)
      .maybeSingle();

    if (tableError) {
      console.error("Error fetching table scan counts:", tableError);
      return { success: true }; // At least the scan was tracked
    }

    return {
      success: true,
      daily_scans: tableData?.daily_scans || 0,
      total_scans: tableData?.total_scans || 0,
    };
  } catch (error) {
    console.error("Error in trackTableScan:", error);
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : "Unknown error tracking table scan",
    };
  }
};

/**
 * Subscribe to all table scans for a store
 * @param storeId - The store ID to subscribe to
 * @param callback - The callback to run when a table is scanned
 * @returns The subscription channel
 */
export const subscribeToAllTableScans = (
  storeId: string,
  callback: (table: any) => void
): RealtimeChannel => {
  const channel = supabase
    .channel("store_tables_channel")
    .on(
      "postgres_changes",
      {
        event: "UPDATE",
        schema: "public",
        table: "store_tables",
        filter: `store_id=eq.${storeId}`,
      },
      (payload) => {
        console.log("Table scan update received:", payload);

        if (payload.new && typeof callback === "function") {
          callback(payload.new);
        }
      }
    )
    .subscribe();

  return channel;
};
